export const FALLBACK_COUNTRY = "in";

const SESSION_KEY = "bytelogic:detected-user-geo";

export type DetectedUserCountry = {
  code: string;
  name: string;
  city: string;
  region: string;
};

const countryFromLocale = (): string | null => {
  if (typeof navigator === "undefined") return null;
  const locale = navigator.language || navigator.languages?.[0] || "";
  const region = locale.split("-")[1]?.toLowerCase();
  return region && /^[a-z]{2}$/.test(region) ? region : null;
};

const readSessionCountry = (): DetectedUserCountry | null => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DetectedUserCountry;
    const code = String(parsed?.code ?? "")
      .trim()
      .toLowerCase();
    if (!/^[a-z]{2}$/.test(code)) return null;
    return {
      code,
      name: String(parsed?.name ?? "").trim(),
      city: String(parsed?.city ?? "").trim(),
      region: String(parsed?.region ?? "").trim(),
    };
  } catch {
    return null;
  }
};

const writeSessionCountry = (country: DetectedUserCountry): void => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(country));
  } catch {
    // ignore quota / private-mode failures
  }
};

export const detectUserCountry = async (): Promise<DetectedUserCountry> => {
  const cached = readSessionCountry();
  if (cached) return cached;

  try {
    const res = await fetch("https://ipwho.is/", {
      signal: AbortSignal.timeout(4000),
    });
    if (res.ok) {
      const data = (await res.json()) as {
        success?: boolean;
        country_code?: string;
        country?: string;
        city?: string;
        region?: string;
      };
      if (data?.success !== false && data?.country_code) {
        const detected: DetectedUserCountry = {
          code: String(data.country_code).toLowerCase(),
          name: String(data.country || "").trim(),
          city: String(data.city || "").trim(),
          region: String(data.region || "").trim(),
        };
        writeSessionCountry(detected);
        return detected;
      }
    }
  } catch {
    // fall through
  }

  const detected: DetectedUserCountry = {
    code: countryFromLocale() || FALLBACK_COUNTRY,
    name: "",
    city: "",
    region: "",
  };
  writeSessionCountry(detected);
  return detected;
};
